import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Search } from 'lucide-react';
import { AreaChart, Area, XAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { useTransactionsQuery } from '../hooks/useQueries';
import { useConfig } from '../context/ConfigContext';
import { useAuth } from '../context/AuthContext';
import { getIconComponent } from '../components/common/IconPicker';

function TransactionSkeleton() {
  return (
    <div className="space-y-2.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-surface-100 animate-pulse">
          <div className="w-9 h-9 rounded-xl bg-surface-200 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="h-3.5 bg-surface-200 rounded w-3/4 mb-2" />
            <div className="h-2.5 bg-surface-100 rounded w-1/2" />
          </div>
          <div className="h-3.5 bg-surface-200 rounded w-16 flex-shrink-0" />
        </div>
      ))}
    </div>
  );
}

export default function CategoryTransactionsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { formatAmount, formatDate, formatTime, config } = useConfig();
  const { hasEncryptionKey } = useAuth();

  const category = location.state?.category;
  const startDate = location.state?.startDate;
  const endDate = location.state?.endDate;
  const categoryName = category?.name;
  const categoryType = category?.type;
  const [searchQuery, setSearchQuery] = useState('');

  // Reset search and scroll to top when category changes
  useEffect(() => {
    setSearchQuery('');
    window.scrollTo(0, 0);
  }, [categoryName, categoryType]);

  // Fetch transactions for this category — current month
  // Use location.key + primitive values to guarantee recompute on every navigation
  const transactionParams = useMemo(() => {
    if (!categoryName) return null;
    const now = new Date();
    const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    return {
      category: category?._id || categoryName,
      type: categoryType,
      startDate: startDate || monthStart,
      ...(endDate && { endDate }),
      limit: 100
    };
  }, [categoryName, categoryType, startDate, endDate, location.key]);

  const { data: transactionData, isLoading } = useTransactionsQuery(
    transactionParams || {},
    { enabled: !!categoryName && hasEncryptionKey, placeholderData: undefined }
  );

  const allTransactions = transactionData?.transactions || [];

  // Filter by search (frontend only)
  const filteredTransactions = useMemo(() => {
    if (!searchQuery.trim()) return allTransactions;
    const q = searchQuery.toLowerCase();
    return allTransactions.filter(tx =>
      (tx.description || '').toLowerCase().includes(q) ||
      (tx.account?.name || tx.bankName || '').toLowerCase().includes(q) ||
      String(tx.amount || tx.amountInINR).includes(q)
    );
  }, [allTransactions, searchQuery]);

  // Group by month (newest first)
  const monthGroups = useMemo(() => {
    const groups = {};
    filteredTransactions.forEach(tx => {
      const monthKey = tx.date?.substring(0, 7); // YYYY-MM
      if (!monthKey) return;
      if (!groups[monthKey]) groups[monthKey] = { key: monthKey, transactions: [], total: 0 };
      groups[monthKey].transactions.push(tx);
      groups[monthKey].total += tx.amount || tx.amountInINR || 0;
    });
    return Object.values(groups).sort((a, b) => b.key.localeCompare(a.key));
  }, [filteredTransactions]);

  // Stats (reflect search filter)
  const stats = useMemo(() => {
    const total = filteredTransactions.reduce((s, tx) => s + (tx.amount || tx.amountInINR || 0), 0);
    const count = filteredTransactions.length;
    const highest = filteredTransactions.reduce((max, tx) => Math.max(max, tx.amount || tx.amountInINR || 0), 0);

    return { total, count, highest };
  }, [filteredTransactions]);

  // Daily spending trend for this month
  const trendData = useMemo(() => {
    const now = new Date();
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const today = now.getDate();
    const data = [];

    for (let day = 1; day <= Math.min(daysInMonth, today); day++) {
      const dateKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayTxs = allTransactions.filter(tx => tx.date?.startsWith(dateKey));
      const total = dayTxs.reduce((s, tx) => s + (tx.amount || tx.amountInINR || 0), 0);
      data.push({ day: String(day), amount: Math.round(total) });
    }
    return data;
  }, [allTransactions]);

  // Format month key to label
  const formatMonthLabel = (key) => {
    const [y, m] = key.split('-');
    const d = new Date(Number(y), Number(m) - 1);
    return d.toLocaleDateString('en', { month: 'long', year: 'numeric' });
  };

  // No category data
  if (!category) {
    return (
      <div className="text-center py-12">
        <p className="text-surface-500">No category selected</p>
        <button
          onClick={() => navigate('/')}
          className="mt-4 font-medium"
          style={{ color: 'var(--accent-bg)' }}
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  const isIncome = category.type === 'income';
  const accentColor = category.color || (isIncome ? config.incomeColor : config.expenseColor);
  const CategoryIcon = getIconComponent(category.icon);

  return (
    <div className="max-w-2xl mx-auto pb-24">
      {/* Header — consistent with TransactionDetailPage */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-xl bg-surface-100 flex items-center justify-center text-surface-600 hover:bg-surface-200 transition-colors"
        >
          <ArrowLeft className="w-[18px] h-[18px]" />
        </button>
        <div className="flex items-center gap-2">
          {CategoryIcon && <CategoryIcon className="w-4.5 h-4.5" style={{ color: accentColor }} />}
          <span className="text-[15px] font-semibold text-surface-900">{category.name}</span>
        </div>
        <div className="w-9 h-9" />
      </div>

      {/* Budget progress */}
      {category.budget > 0 && (
        <div className="bg-white rounded-xl border border-surface-200 p-3 mb-4">
          <div className="flex justify-between text-xs mb-1.5">
            <span className="text-surface-500">Budget: {formatAmount(category.budget)}</span>
            <span className="font-semibold" style={{ color: accentColor }}>
              {Math.round((stats.total / category.budget) * 100)}% used
            </span>
          </div>
          <div className="h-1.5 bg-surface-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${Math.min((stats.total / category.budget) * 100, 100)}%`, backgroundColor: accentColor }}
            />
          </div>
        </div>
      )}

      {/* Spending Trend — daily this month */}
      {allTransactions.length > 0 && (
        <div className="bg-white rounded-2xl border border-surface-200 p-4 mb-4">
          <p className="text-sm font-semibold text-surface-900 mb-3">Spending Trend</p>
          <ResponsiveContainer width="100%" height={100}>
            <AreaChart data={trendData}>
              <defs>
                <linearGradient id="catTrendGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={accentColor} stopOpacity={0.3} />
                  <stop offset="100%" stopColor={accentColor} stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="day"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: '#999' }}
                interval="preserveStartEnd"
              />
              <Tooltip
                formatter={(v) => [formatAmount(v), 'Amount']}
                contentStyle={{ borderRadius: 8, border: '1px solid #eee', fontSize: 12 }}
              />
              <Area
                type="monotone"
                dataKey="amount"
                stroke={accentColor}
                strokeWidth={2}
                fill="url(#catTrendGrad)"
                dot={{ r: 3, fill: accentColor, strokeWidth: 0 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="rounded-xl p-3 text-center" style={{ backgroundColor: `${accentColor}10` }}>
          <p className="text-sm font-bold" style={{ color: accentColor }}>{formatAmount(stats.total)}</p>
          <p className="text-[10px] text-surface-500 mt-0.5">Total Spent</p>
        </div>
        <div className="rounded-xl p-3 text-center" style={{ backgroundColor: `${accentColor}10` }}>
          <p className="text-sm font-bold" style={{ color: accentColor }}>{stats.count}</p>
          <p className="text-[10px] text-surface-500 mt-0.5">Transactions</p>
        </div>
        <div className="rounded-xl p-3 text-center" style={{ backgroundColor: `${accentColor}10` }}>
          <p className="text-sm font-bold" style={{ color: accentColor }}>{formatAmount(stats.highest)}</p>
          <p className="text-[10px] text-surface-500 mt-0.5">Highest</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm">🔍</span>
        <input
          type="text"
          placeholder="Search transactions..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="w-full pl-9 pr-4 py-2.5 bg-white rounded-xl border border-surface-200 text-sm placeholder:text-surface-400 focus:outline-none focus:border-surface-300"
        />
      </div>

      {/* Grouped Transactions */}
      {isLoading ? (
        <TransactionSkeleton />
      ) : monthGroups.length > 0 ? (
        <div className="space-y-5">
          {monthGroups.map(group => (
            <div key={group.key}>
              <div className="flex items-center justify-between mb-2 px-1">
                <p className="text-xs font-semibold text-surface-500 uppercase tracking-wide">
                  {formatMonthLabel(group.key)}
                </p>
                <p className="text-xs font-bold" style={{ color: accentColor }}>
                  {formatAmount(group.total)}
                </p>
              </div>
              <div className="space-y-2">
                {group.transactions.map(tx => {
                  const dateStr = formatDate(tx.date);
                  const shortDate = dateStr.replace(/[-/]\d{4}$/, '').replace(/^\d{4}[-/]/, '');

                  return (
                    <button
                      key={tx._id}
                      onClick={() => navigate(`/transactions/${tx._id}`)}
                      className="w-full flex items-center gap-3 p-3 bg-white rounded-xl border border-surface-100 hover:border-surface-200 active:bg-surface-50 transition-all text-left"
                    >
                      <div
                        className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${accentColor}12` }}
                      >
                        {CategoryIcon
                          ? <CategoryIcon className="w-4 h-4" style={{ color: accentColor }} />
                          : <ChevronRight className="w-4 h-4" style={{ color: accentColor }} />
                        }
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-surface-900 truncate">
                          {tx.description || tx.category?.name || tx.category}
                        </p>
                        <p className="text-xs text-surface-400 truncate mt-0.5">
                          {shortDate}{tx.time && `, ${formatTime(tx.time)}`} · {tx.account?.name || tx.bankName}
                        </p>
                      </div>
                      <span
                        className="text-sm font-bold flex-shrink-0"
                        style={{ color: accentColor }}
                      >
                        {formatAmount(tx.amount || tx.amountInINR)}
                      </span>
                      <ChevronRight className="w-4 h-4 text-surface-300 flex-shrink-0" />
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          {searchQuery ? (
            <>
              <p className="text-surface-500 text-sm">No transactions match "{searchQuery}"</p>
              <button
                onClick={() => setSearchQuery('')}
                className="mt-2 text-sm font-medium"
                style={{ color: accentColor }}
              >
                Clear search
              </button>
            </>
          ) : (
            <>
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3"
                style={{ backgroundColor: `${accentColor}10` }}
              >
                {CategoryIcon
                  ? <CategoryIcon className="w-7 h-7" style={{ color: accentColor }} />
                  : null
                }
              </div>
              <p className="text-surface-600 font-medium">No transactions found</p>
              <p className="text-surface-400 text-sm mt-1">Add one to get started</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
